import { useCallback } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslate } from "@refinedev/core";
import { Badge, Box, Button, Popover, Stack } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

import { useFilterDropdownController } from "./controller";
import { FilterDropdownProps } from "./types";

export function FilterDropdown({
  initialValues,
  paramPrefix,
  children,
}: FilterDropdownProps) {
  const translate = useTranslate();

  const {
    open,
    methods,
    anchorEl,
    hasActiveFilters,
    handleClick,
    handleClose,
    handleReset,
    handleSubmit,
  } = useFilterDropdownController({ initialValues, paramPrefix });

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement
    ) {
      event.stopPropagation();
    }
  }, []);

  return (
    <div>
      <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
        <Button
          id="filter-button"
          aria-controls={open ? "filter-popover" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          variant={hasActiveFilters ? "outlined" : "text"}
          startIcon={<FilterAltIcon />}
        >
          {translate("buttons.filter")}
        </Button>
      </Badge>

      <Popover
        id="filter-popover"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { padding: 2, width: 400 },
        }}
      >
        <Stack spacing={3}>
          <FormProvider {...methods}>
            <Box
              component="form"
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              autoComplete="off"
              onSubmit={handleSubmit}
              onKeyDown={handleKeyDown}
            >
              {children}

              <Stack spacing={2} direction="row" justifyContent="flex-end">
                <Button onClick={handleReset} variant="outlined">
                  {translate("buttons.reset")}
                </Button>
                <Button type="submit" variant="contained">
                  {translate("buttons.search")}
                </Button>
              </Stack>
            </Box>
          </FormProvider>
        </Stack>
      </Popover>
    </div>
  );
}
